import React, { useState } from "react";
import { useDebounce } from "use-debounce";
import {
  Table,
  Button,
  Form,
  Input,
  message,
  Empty,
  Drawer,
  Spin,
  Space,
  Tag,
} from "antd";
import {
  EditOutlined,
  SearchOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import { useProductsQuery, useUpdateProductMutation, useSectionAccess } from "../../Services";
import { Product, ProductAction } from "../../Types";
import { PAGE_SIZE, DEBOUNCED_SEARCH_QUERY } from "../../Constant";
import {
  PageHeader,
  tablePagination,
  DRAWER_FORM_WIDTH,
  drawerFooter,
  drawerTitle,
} from "../../components";
import { StockForm } from "../products/common/StockForm";
import dayjs from "dayjs";

export const StockAdjustmentPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search] = useDebounce(searchInput, DEBOUNCED_SEARCH_QUERY);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [form] = Form.useForm();

  // Query products list with search and pagination
  const { data: productsData, isLoading, refetch } = useProductsQuery({
    page,
    limit: PAGE_SIZE,
    search,
  });

  const updateProductMutation = useUpdateProductMutation();
  const { isAllowed } = useSectionAccess("products");

  const productsList = productsData?.data || [];
  const total = productsData?.total || 0;

  const openDrawer = (product: Product) => {
    setEditingProduct(product);
    
    // Map existing stocks and parse dates for DatePicker
    const mappedStocks = (product.stocks || []).map((s: any) => ({
      ...s,
      expiry_date: s.expiry_date ? dayjs(s.expiry_date) : null,
    }));

    form.setFieldsValue({
      stocks: mappedStocks,
    });
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingProduct(null);
    form.resetFields();
  };

  const handleSubmit = async () => {
    if (!editingProduct) return;

    try {
      const values = await form.validateFields();
      
      const formData = new FormData();
      formData.append("id", String(editingProduct.id));
      formData.append("title", editingProduct.title);
      formData.append("category_id", String(editingProduct.category_id));
      formData.append("brand_id", String(editingProduct.brand_id));
      formData.append("unit", editingProduct.unit);
      formData.append("sku_code", editingProduct.sku_code);
      formData.append("mrp", String(editingProduct.mrp));
      formData.append("alert_quantity", String(editingProduct.alert_quantity || 10));
      formData.append("description", editingProduct.description || "");
      formData.append("barcode", editingProduct.barcode || "");
      formData.append(
        "is_negative_qty_allowed",
        String(editingProduct.is_negative_qty_allowed ? 1 : 0),
      );
      formData.append("printer_id", editingProduct.printer_id || "");
      formData.append("store_id", String((editingProduct as any).store_id || ""));

      const suppliers = (editingProduct.suppliers || []).map((s: any) => s.id);
      formData.append("suppliers", JSON.stringify(suppliers));

      const stockBatches = (values.stocks || []).map((s: any) => ({
        ...s,
        expiry_date: s.expiry_date && typeof s.expiry_date.format === "function"
          ? s.expiry_date.format("YYYY-MM-DD")
          : s.expiry_date || null,
      }));
      formData.append("stocks", JSON.stringify(stockBatches));

      let imgs: string[] = [];
      try {
        imgs = JSON.parse(editingProduct.image_paths || "[]");
      } catch {
        imgs = [];
      }
      formData.append("existing_images", JSON.stringify(imgs));

      const res = await updateProductMutation.mutateAsync(formData);

      if (res?.success) {
        message.success("Stock adjustments saved successfully!");
        refetch();
        closeDrawer();
      } else {
        if (res?.errors) {
          Object.entries(res.errors).forEach(([field, msg]) => {
            if (field.startsWith("stocks_")) {
              message.error(`${msg}`);
            }
          });
        } else {
          message.error(res?.message || "Failed to update stocks.");
        }
      }
    } catch (error) {
      message.error("Please fill all the required stock details correctly.");
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(e.target.value);
    setPage(1);
  };

  const columns = [
    {
      title: "SKU Code",
      dataIndex: "sku_code",
      key: "sku_code",
      render: (v: string) => <span className="font-mono text-xs">{v || "N/A"}</span>,
    },
    {
      title: "Product Name",
      dataIndex: "title",
      key: "title",
      render: (v: string) => <span className="font-semibold text-gray-800">{v}</span>,
    },
    {
      title: "Barcode",
      dataIndex: "barcode",
      key: "barcode",
      render: (v: string) => <span className="text-gray-500 font-mono text-xs">{v || "N/A"}</span>,
    },
    {
      title: "Total Quantity",
      dataIndex: "total_quantity",
      key: "total_quantity",
      render: (v: number) => (
        <Tag color={v <= 0 ? "error" : v < 10 ? "warning" : "success"} className="font-semibold">
          {v}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 150,
      render: (_: any, rec: Product) => (
        <Space size="middle">
          {isAllowed(ProductAction.StockAdjustment) && (
            <Button
              type="primary"
              icon={<EditOutlined />}
              size="small"
              onClick={() => openDrawer(rec)}
            >
              Adjust Stock
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="p-6 min-h-screen bg-gray-50">
      <PageHeader
        title="Stock Adjustments"
        subText={`Total unique products: ${total}`}
        handleButtons={[
          {
            title: "",
            icon: ReloadOutlined,
            onClick: refetch,
          },
        ]}
        searchBar={
          <Input
            placeholder="Search by title, SKU or barcode..."
            prefix={<SearchOutlined />}
            value={searchInput}
            onChange={handleSearchChange}
            allowClear
            onClear={() => {
              setSearchInput("");
              setPage(1);
            }}
            className="w-full sm:w-64"
          />
        }
      />

      <div className="mt-4 bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
        <Table
          dataSource={productsList}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={tablePagination({
            current: page,
            pageSize: PAGE_SIZE,
            total,
            onChange: setPage,
            itemName: "products",
          })}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No products found"
              />
            ),
          }}
        />
      </div>

      <Drawer
        title={drawerTitle(
          "Adjust Stock Batches",
          editingProduct ? `${editingProduct.title} (SKU: ${editingProduct.sku_code})` : "",
        )}
        open={drawerOpen}
        onClose={closeDrawer}
        width={DRAWER_FORM_WIDTH + 150} // Slightly wider drawer for the tabular stock list
        footer={drawerFooter({
          actions: [
            { label: "Cancel", onClick: closeDrawer },
            {
              label: "Save Adjustments",
              type: "primary",
              loading: updateProductMutation.isPending,
              onClick: handleSubmit,
            },
          ],
        })}
      >
        <Spin spinning={updateProductMutation.isPending}>
          <Form form={form} layout="vertical">
            <StockForm form={form} />
          </Form>
        </Spin>
      </Drawer>
    </div>
  );
};
